import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_BASE_URL, apiGet, apiPost, apiUpload, apiDelete } from '../services/api';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import OMRCardPreview2x1 from '../components/answerCard/OMRCardPreview2x1';
import SearchableSelect from '../components/SearchableSelect';
import { useOMRCardGeneration } from '../hooks/useOMRCardGeneration';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';

function Provas() {
  const { getUserCity, hasRole } = useAuth();
  const { darkMode } = useTheme();
  const { isPreviewOpen, examData, openPreview, closePreview } = useOMRCardGeneration();
  getUserCity();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('arquivos');

  const [exams, setExams] = useState([]);
  const [examsPage] = useState(1);
  const [examsTotal, setExamsTotal] = useState(0);
  const [s3Files, setS3Files] = useState([]);
  const [s3Info, setS3Info] = useState({ storage: '', bucket: '', region: '' });
  const [s3Loading, setS3Loading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ file: null, progress: 0, status: null, message: '' });
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('');

  // Modal de confirmação de exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'file' ou 'exam'

  // Modal de agendamento de prova
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [examToSchedule, setExamToSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduling, setScheduling] = useState(false);

  // Modal de criar nova prova
  const [showCreateExamModal, setShowCreateExamModal] = useState(false);
  const [creatingExam, setCreatingExam] = useState(false);
  const [createExamError, setCreateExamError] = useState('');
  const [newExamData, setNewExamData] = useState({
    exam_code: '',
    exam_name: '',
    subject: '',
    school_year: new Date().getFullYear().toString(),
    total_questions: '',
    description: '',
    scheduled_date_value: '',
    scheduled_time_value: '',
  });

  const fetchExams = async () => {
    try {
      const response = await apiGet('/exams/', { page: examsPage, page_size: 20 });
      setExams(response.results || []);
      setExamsTotal(response.count || 0);
    } catch (error) {
      console.error('Erro ao buscar provas:', error);
    }
  };

  const fetchS3Files = async () => {
    setS3Loading(true);
    try {
      const response = await apiGet('/exams/list_s3_files/');
      if (response.success) {
        setS3Files(response.files || []);
        setS3Info({ storage: response.storage, bucket: response.bucket || 'Local', region: response.region || 'N/A' });
      }
    } catch (error) {
      console.error('Erro ao buscar arquivos S3:', error);
    } finally {
      setS3Loading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchExams(), fetchS3Files()]);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examsPage]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const icons = { pdf: '📄', jpg: '🖼', jpeg: '🖼', png: '🖼', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊', csv: '📊' };
    return icons[ext] || '📁';
  };

  const isImage = (fileName) => ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileName.split('.').pop().toLowerCase());
  const isPDF = (fileName) => fileName.toLowerCase().endsWith('.pdf');

  const handleFileSelect = (file) => {
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      setUploadStatus({ file: null, status: 'error', message: `Formato inválido. Use: ${allowedExtensions.join(', ')}` });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadStatus({ file: null, status: 'error', message: 'Arquivo muito grande. Maximo: 50MB' });
      return;
    }
    setUploadStatus({ file, status: 'ready', message: `Arquivo selecionado: ${file.name}`, progress: 0 });
  };

  const handleUpload = async () => {
    if (!uploadStatus.file) return;
    setUploadStatus(prev => ({ ...prev, status: 'uploading', progress: 0 }));
    try {
      const examCode = `PROVA_${Date.now()}`;
      const exam = await apiPost('/exams/', { exam_code: examCode, exam_name: uploadStatus.file.name.replace(/\.[^/.]+$/, ''), subject: 'Geral', school_year: new Date().getFullYear().toString(), total_questions: 0 });
      const formData = new FormData();
      formData.append('file', uploadStatus.file);
      await apiUpload(`/exams/${exam.id}/upload_file/`, formData, (progress) => setUploadStatus(prev => ({ ...prev, progress })));
      setUploadStatus({ file: null, status: 'success', message: 'Arquivo enviado com sucesso!', progress: 100 });
      await fetchS3Files();
      await fetchExams();
      setTimeout(() => setUploadStatus({ file: null, status: null, message: '', progress: 0 }), 3000);
    } catch (error) {
      setUploadStatus({ file: null, status: 'error', message: error.message || 'Erro ao fazer upload', progress: 0 });
    }
  };

  const handleDeleteFileClick = (file) => {
    setItemToDelete(file);
    setDeleteType('file');
    setShowDeleteModal(true);
  };

  const handleDeleteExamClick = (exam) => {
    setItemToDelete(exam);
    setDeleteType('exam');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (item) => {
    if (deleteType === 'file') {
      const response = await fetch(`${API_BASE_URL}/exams/delete_s3_file/`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ key: item.key })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Erro ao excluir arquivo');
      }
      await fetchS3Files();
    } else {
      await apiDelete(`/exams/${item.id}/`);
      await fetchExams();
      await fetchS3Files();
    }
  };

  const handleScheduleClick = (exam) => {
    setExamToSchedule(exam);
    if (exam.scheduled_date) {
      const d = new Date(exam.scheduled_date);
      setScheduleDate(d.toISOString().split('T')[0]);
      setScheduleTime(d.toTimeString().slice(0, 5));
    } else {
      setScheduleDate('');
      setScheduleTime('');
    }
    setShowScheduleModal(true);
  };

  const handleScheduleConfirm = async () => {
    if (!scheduleDate || !examToSchedule) return;
    setScheduling(true);
    try {
      const scheduledDateTime = scheduleTime
        ? `${scheduleDate}T${scheduleTime}:00`
        : `${scheduleDate}T00:00:00`;
      await apiPost(`/exams/${examToSchedule.id}/schedule/`, { scheduled_date: scheduledDateTime });
      setShowScheduleModal(false);
      setExamToSchedule(null);
      await fetchExams();
    } catch (error) {
      console.error('Erro ao agendar prova:', error);
    } finally {
      setScheduling(false);
    }
  };

  const handleOpenCreateExam = () => {
    setNewExamData({
      exam_code: '',
      exam_name: '',
      subject: '',
      school_year: new Date().getFullYear().toString(),
      total_questions: '',
      description: '',
      scheduled_date_value: '',
      scheduled_time_value: '',
    });
    setCreateExamError('');
    setShowCreateExamModal(true);
  };

  const handleCreateExamConfirm = async () => {
    if (!newExamData.exam_code.trim() || !newExamData.exam_name.trim()) {
      setCreateExamError('Código e nome da prova são obrigatórios.');
      return;
    }
    setCreatingExam(true);
    setCreateExamError('');
    try {
      const payload = {
        exam_code: newExamData.exam_code.trim(),
        exam_name: newExamData.exam_name.trim(),
        subject: newExamData.subject || null,
        school_year: newExamData.school_year ? parseInt(newExamData.school_year) : null,
        total_questions: newExamData.total_questions ? parseInt(newExamData.total_questions) : null,
        description: newExamData.description || null,
      };
      if (newExamData.scheduled_date_value) {
        payload.scheduled_date = newExamData.scheduled_time_value
          ? `${newExamData.scheduled_date_value}T${newExamData.scheduled_time_value}:00`
          : `${newExamData.scheduled_date_value}T00:00:00`;
      }
      await apiPost('/exams/', payload);
      setShowCreateExamModal(false);
      await fetchExams();
    } catch (error) {
      setCreateExamError(error.message || 'Erro ao criar prova');
    } finally {
      setCreatingExam(false);
    }
  };

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#13983D]', 'bg-[#13983D]/10'); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.currentTarget.classList.remove('border-[#13983D]', 'bg-[#13983D]/10'); }, []);
  const handleDrop = useCallback((e) => { e.preventDefault(); e.currentTarget.classList.remove('border-[#13983D]', 'bg-[#13983D]/10'); if (e.dataTransfer.files.length > 0) handleFileSelect(e.dataTransfer.files[0]); }, []);

  const filteredFiles = s3Files.filter(file => !searchTerm || file.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredExams = exams.filter(exam => (!searchTerm || exam.exam_name?.toLowerCase().includes(searchTerm.toLowerCase()) || exam.exam_code?.toLowerCase().includes(searchTerm.toLowerCase())) && (!filterSubject || exam.subject === filterSubject));
  const subjects = [...new Set(exams.map(e => e.subject).filter(Boolean))];

  return (
    <>
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode ? 'bg-gradient-to-r from-gray-50 to-gray-400' : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'}`}>
            Provas e Arquivos
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Gerencie provas e arquivos do bucket S3
            {s3Info.storage === 's3' && <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[#13983D]/20 text-[#13983D]">S3: {s3Info.bucket}</span>}
            {s3Info.storage === 'local' && <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[#F9B047]/20 text-[#F9B047]">Storage Local</span>}
          </p>
        </div>
        <div className="flex gap-3">
          {hasRole(ROLES.ADMIN) && (
            <button onClick={handleOpenCreateExam} className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-all bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25">
              + Nova Prova
            </button>
          )}
          <button onClick={() => { fetchS3Files(); fetchExams(); }} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
            Atualizar
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[{ label: 'Arquivos S3', value: s3Files.length, icon: '📁' }, { label: 'Provas Cadastradas', value: examsTotal, icon: '📝' }, { label: 'Tamanho Total', value: formatFileSize(s3Files.reduce((acc, f) => acc + f.size, 0)), icon: '💾' }, { label: 'Storage', value: s3Info.storage === 's3' ? 'AWS S3' : 'Local', icon: '☁' }].map(stat => (
          <div key={stat.label} className={`rounded-xl p-4 text-center ${darkMode ? 'bg-slate-800/50 border border-slate-700/10' : 'bg-white/80 border border-slate-200 shadow-sm'}`}>
            <div className="text-2xl mb-1">{stat.icon}</div>
            <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">{stat.value}</div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{stat.label}</div>
          </div>
        ))}
      </div>

      <div className={`rounded-2xl p-1.5 flex gap-1 mb-6 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
        {[{ id: 'provas', label: 'Provas Cadastradas', icon: '📝' }, { id: 'arquivos', label: 'Arquivos S3', icon: '📁' }, { id: 'upload', label: 'Novo Upload', icon: '📤' }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === tab.id ? 'text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974]' : darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50' : 'text-slate-500 hover:text-slate-700 hover:bg-white'}`}>
            <span>{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      <div className={`flex gap-4 mb-6 ${activeTab === 'upload' ? 'hidden' : ''}`}>
        <div className="flex-1 relative">
          <input type="text" placeholder="Buscar arquivos ou provas..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full px-4 py-3 pl-10 rounded-xl border transition-all ${darkMode ? 'bg-slate-800/50 border-slate-700/50 text-slate-200 placeholder-slate-500 focus:border-[#0974BA]' : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-[#0974BA]'}`} />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
        </div>
        {activeTab === 'provas' && (
          <SearchableSelect
            value={filterSubject}
            onChange={setFilterSubject}
            options={subjects}
            emptyOptionLabel="Todas as disciplinas"
            darkMode={darkMode}
            className="min-w-[200px]"
          />
        )}
      </div>

      {loading ? (
        <LoadingSpinnerAdvanced
          darkMode={darkMode}
          message="Carregando Provas..."
          subtitle="Buscando avaliações e arquivos..."
          size="lg"
        />
      ) : activeTab === 'arquivos' ? (
        <div className={`rounded-2xl p-6 ${darkMode ? 'bg-slate-800/50 border border-slate-700/10' : 'bg-white/80 border border-slate-200'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Arquivos no {s3Info.storage === 's3' ? `Bucket S3 (${s3Info.bucket})` : 'Storage Local'}</h3>
          {s3Loading ? <div className="flex items-center justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0974BA]"></div></div> : filteredFiles.length === 0 ? (
            <div className={`text-center py-10 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}><div className="text-4xl mb-3">📭</div><p>Nenhum arquivo encontrado</p><button onClick={() => setActiveTab('upload')} className="mt-4 px-4 py-2 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974]">Fazer Upload</button></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredFiles.map((file, index) => (
                <div key={index} className={`rounded-xl p-4 border transition-all hover:shadow-lg cursor-pointer ${darkMode ? 'bg-slate-900/50 border-slate-700/50 hover:border-[#0974BA]/50' : 'bg-slate-50 border-slate-200 hover:border-[#0974BA]/60'}`}>
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{getFileIcon(file.name)}</div>
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium truncate ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{file.name}</h4>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{formatFileSize(file.size)}</p>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{formatDate(file.last_modified)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-700/20">
                    <a href={file.url} target="_blank" rel="noopener noreferrer" className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium text-center transition-all ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA] hover:bg-[#0974BA]/30' : 'bg-[#0974BA]/15 text-[#0974BA] hover:bg-[#0974BA]/25'}`}>Abrir</a>
                    {(isImage(file.name) || isPDF(file.name)) && <button onClick={() => { setSelectedFile(file); setShowPreview(true); }} className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${darkMode ? 'bg-[#2E4974]/20 text-[#2E4974] hover:bg-[#2E4974]/30' : 'bg-[#2E4974]/15 text-[#2E4974] hover:bg-[#2E4974]/25'}`}>Preview</button>}
                    {hasRole(ROLES.ADMIN) && <button onClick={() => handleDeleteFileClick(file)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${darkMode ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30' : 'bg-[#E83A3C]/15 text-[#E83A3C] hover:bg-[#E83A3C]/25'}`}>Excluir</button>}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === 'provas' ? (
        <div className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-slate-800/50 border border-slate-700/10' : 'bg-white/80 border border-slate-200'}`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={darkMode ? 'bg-slate-900/50' : 'bg-slate-50'}>
                <tr>
                  {['Código', 'Nome', 'Disciplina', 'Ano', 'Questões', 'Agendamento', 'Arquivo', 'Ações'].map(h => <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/20">
                {filteredExams.length === 0 ? <tr><td colSpan="8" className={`px-4 py-10 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Nenhuma prova encontrada</td></tr> : filteredExams.map((exam) => (
                  <tr key={exam.id} className={darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}>
                    <td className={`px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}><span className="font-mono text-xs">{exam.exam_code}</span></td>
                    <td className={`px-4 py-3 font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{exam.exam_name}</td>
                    <td className={`px-4 py-3`}><span className={`px-2 py-1 rounded-full text-xs ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/15 text-[#0974BA]'}`}>{exam.subject || 'N/A'}</span></td>
                    <td className={`px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{exam.school_year}</td>
                    <td className={`px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{exam.total_questions || 0}</td>
                    <td className={`px-4 py-3`}>
                      {exam.scheduled_date ? (
                        <span className={`px-2 py-1 rounded-full text-xs ${new Date(exam.scheduled_date) <= new Date()
                          ? darkMode ? 'bg-[#13983D]/20 text-[#13983D]' : 'bg-[#13983D]/15 text-[#13983D]'
                          : darkMode ? 'bg-[#F9B047]/20 text-[#F9B047]' : 'bg-[#F9B047]/15 text-[#F9B047]'
                          }`}>
                          {new Date(exam.scheduled_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      ) : (
                        <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sem agendamento</span>
                      )}
                    </td>
                    <td className={`px-4 py-3`}>{exam.exam_file ? <a href={exam.exam_file} target="_blank" rel="noopener noreferrer" className="text-[#0974BA] hover:text-[#0974BA]/70">Ver arquivo</a> : <span className="text-slate-500">-</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-[#2E4974]/20 text-[#2E4974] hover:bg-[#2E4974]/30' : 'bg-[#2E4974]/15 text-[#2E4974] hover:bg-[#2E4974]/25'}`}>Detalhes</button>
                        {hasRole(ROLES.ADMIN) && (
                          <button onClick={() => openPreview(exam.id, exam.exam_name)} className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA] hover:bg-[#0974BA]/30' : 'bg-[#0974BA]/15 text-[#0974BA] hover:bg-[#0974BA]/25'}`}>
                            Cartão OMR
                          </button>
                        )}
                        {hasRole(ROLES.ADMIN) && (
                          <button onClick={() => handleScheduleClick(exam)} className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-[#13983D]/20 text-[#13983D] hover:bg-[#13983D]/30' : 'bg-[#13983D]/15 text-[#13983D] hover:bg-[#13983D]/25'}`}>
                            Agendar Prova
                          </button>
                        )}
                        {hasRole(ROLES.ADMIN) && <button onClick={() => handleDeleteExamClick(exam)} className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30' : 'bg-[#E83A3C]/15 text-[#E83A3C] hover:bg-[#E83A3C]/25'}`}>Excluir</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={`rounded-2xl p-6 ${darkMode ? 'bg-slate-800/50 border border-slate-700/10' : 'bg-white/80 border border-slate-200'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Upload de Arquivo de Prova</h3>
          <div className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer ${darkMode ? 'border-slate-600/50 bg-slate-900/30 hover:border-[#0974BA] hover:bg-[#0974BA]/5' : 'border-slate-300 bg-slate-50 hover:border-[#0974BA] hover:bg-[#0974BA]/10'} ${uploadStatus.status === 'uploading' ? 'pointer-events-none opacity-50' : ''}`} onClick={() => fileInputRef.current?.click()} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])} />
            {uploadStatus.file ? (
              <div className="space-y-3"><div className="text-4xl">{getFileIcon(uploadStatus.file.name)}</div><p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{uploadStatus.file.name}</p><p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{formatFileSize(uploadStatus.file.size)}</p></div>
            ) : (
              <><div className="text-5xl mb-4">📁</div><h4 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Arraste seu arquivo aqui</h4><p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>ou clique para selecionar</p><div className="flex gap-2 justify-center flex-wrap">{['.PDF', '.JPG', '.PNG', '.DOC', '.DOCX'].map(format => <span key={format} className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/15 text-[#0974BA]'}`}>{format}</span>)}</div></>
            )}
          </div>
          {uploadStatus.status === 'uploading' && <div className="mt-4"><div className="flex justify-between text-sm mb-2"><span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Enviando...</span><span className={darkMode ? 'text-slate-300' : 'text-slate-600'}>{uploadStatus.progress}%</span></div><div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}><div className="h-full bg-gradient-to-r from-[#0974BA] to-[#2E4974] transition-all duration-300" style={{ width: `${uploadStatus.progress}%` }} /></div></div>}
          {uploadStatus.message && <div className={`mt-4 p-4 rounded-xl ${uploadStatus.status === 'success' ? darkMode ? 'bg-[#13983D]/10 border border-[#13983D]/20' : 'bg-[#13983D]/10 border border-[#13983D]/30' : uploadStatus.status === 'error' ? darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/10 border border-[#E83A3C]/30' : darkMode ? 'bg-[#0974BA]/10 border border-[#0974BA]/20' : 'bg-[#0974BA]/10 border border-[#0974BA]/25'}`}><p className={`text-sm font-medium ${uploadStatus.status === 'success' ? darkMode ? 'text-[#13983D]' : 'text-[#13983D]' : uploadStatus.status === 'error' ? darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]' : darkMode ? 'text-[#0974BA]' : 'text-[#0974BA]'}`}>{uploadStatus.status === 'success' ? '✓' : uploadStatus.status === 'error' ? '✕' : '•'} {uploadStatus.message}</p></div>}
          <div className="flex gap-3 justify-end mt-6 pt-5 border-t border-slate-700/10">
            <button onClick={() => { setUploadStatus({ file: null, status: null, message: '', progress: 0 }); if (fileInputRef.current) fileInputRef.current.value = ''; }} className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>Limpar</button>
            <button onClick={handleUpload} disabled={!uploadStatus.file || uploadStatus.status === 'uploading'} className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${!uploadStatus.file || uploadStatus.status === 'uploading' ? 'bg-slate-500 cursor-not-allowed' : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'}`}>{uploadStatus.status === 'uploading' ? 'Enviando...' : 'Enviar para S3'}</button>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão com Senha */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
          setDeleteType('');
        }}
        onConfirm={handleDeleteConfirm}
        item={itemToDelete}
        itemName={deleteType === 'file' ? itemToDelete?.name : itemToDelete?.exam_name}
        itemType={deleteType === 'file' ? 'Arquivo' : 'Prova'}
        warningMessage={deleteType === 'file'
          ? 'O arquivo será removido permanentemente do storage. Digite sua senha para confirmar.'
          : 'A prova e seus dados serão removidos. Digite sua senha para confirmar.'
        }
      />

      {/* Modal de Agendamento de Prova */}
      {showScheduleModal && examToSchedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowScheduleModal(false)}>
          <div className={`relative w-full max-w-md rounded-2xl overflow-hidden ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200 shadow-xl'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Agendar Prova</h3>
              <button onClick={() => setShowScheduleModal(false)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>X</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className={`text-sm mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Prova</p>
                <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{examToSchedule.exam_name}</p>
                <p className={`text-xs font-mono ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{examToSchedule.exam_code}</p>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Data de disponibilizacao *
                </label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Horario (opcional)
                </label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    }`}
                />
              </div>
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#F9B047]/10 border border-[#F9B047]/20' : 'bg-[#F9B047]/10 border border-[#F9B047]/25'}`}>
                <p className={`text-xs ${darkMode ? 'text-[#F9B047]' : 'text-[#F9B047]'}`}>
                  A prova ficara visivel para os coordenadores somente apos a data e horario agendados.
                </p>
              </div>
            </div>
            <div className={`flex gap-3 justify-end p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <button
                onClick={() => setShowScheduleModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
              >
                Cancelar
              </button>
              <button
                onClick={handleScheduleConfirm}
                disabled={!scheduleDate || scheduling}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${!scheduleDate || scheduling
                  ? 'bg-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#13983D] to-[#0d7a30] hover:shadow-lg hover:shadow-[#13983D]/25'
                  }`}
              >
                {scheduling ? 'Agendando...' : 'Confirmar Agendamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criar Nova Prova */}
      {showCreateExamModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowCreateExamModal(false)}>
          <div className={`relative w-full max-w-lg rounded-2xl overflow-hidden ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200 shadow-xl'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Criar Nova Prova</h3>
              <button onClick={() => setShowCreateExamModal(false)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>X</button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Código da Prova *</label>
                  <input type="text" value={newExamData.exam_code} onChange={(e) => setNewExamData({ ...newExamData, exam_code: e.target.value })} placeholder="Ex: PROVA_MAT_2026" className={`w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'}`} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Ano Letivo</label>
                  <input type="number" value={newExamData.school_year} onChange={(e) => setNewExamData({ ...newExamData, school_year: e.target.value })} className={`w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-200 text-slate-700'}`} />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Nome da Prova *</label>
                <input type="text" value={newExamData.exam_name} onChange={(e) => setNewExamData({ ...newExamData, exam_name: e.target.value })} placeholder="Ex: Avaliação de Matemática - 5o Ano" className={`w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'}`} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Disciplina</label>
                  <input type="text" value={newExamData.subject} onChange={(e) => setNewExamData({ ...newExamData, subject: e.target.value })} placeholder="Ex: Matemática" className={`w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'}`} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Total de Questoes</label>
                  <input type="number" value={newExamData.total_questions} onChange={(e) => setNewExamData({ ...newExamData, total_questions: e.target.value })} placeholder="0" min="0" className={`w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'}`} />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Descrição</label>
                <textarea value={newExamData.description} onChange={(e) => setNewExamData({ ...newExamData, description: e.target.value })} placeholder="Descrição da prova (opcional)" rows={3} className={`w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 resize-none ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'}`} />
              </div>
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#0974BA]/10 border border-[#0974BA]/20' : 'bg-[#0974BA]/10 border border-[#0974BA]/25'}`}>
                <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-[#0974BA]' : 'text-[#0974BA]'}`}>Agendar disponibilizacao (opcional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={newExamData.scheduled_date_value} onChange={(e) => setNewExamData({ ...newExamData, scheduled_date_value: e.target.value })} className={`w-full px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-200 text-slate-700'}`} />
                  <input type="time" value={newExamData.scheduled_time_value} onChange={(e) => setNewExamData({ ...newExamData, scheduled_time_value: e.target.value })} className={`w-full px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-200 text-slate-700'}`} />
                </div>
              </div>
              {createExamError && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/10 border border-[#E83A3C]/30'}`}>
                  <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{createExamError}</p>
                </div>
              )}
            </div>
            <div className={`flex gap-3 justify-end p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <button onClick={() => setShowCreateExamModal(false)} className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                Cancelar
              </button>
              <button onClick={handleCreateExamConfirm} disabled={creatingExam} className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${creatingExam ? 'bg-slate-500 cursor-not-allowed' : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'}`}>
                {creatingExam ? 'Criando...' : 'Criar Prova'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreview && selectedFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setShowPreview(false)}>
          <div className={`relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden ${darkMode ? 'bg-slate-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <h3 className={`font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{selectedFile.name}</h3>
              <button onClick={() => setShowPreview(false)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>X</button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              {isImage(selectedFile.name) ? <img src={selectedFile.url} alt={selectedFile.name} className="max-w-full h-auto mx-auto rounded-lg" /> : isPDF(selectedFile.name) ? <iframe src={selectedFile.url} className="w-full h-[70vh] rounded-lg" title={selectedFile.name} /> : <div className="text-center py-10"><p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Preview não disponível</p><a href={selectedFile.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-4 px-4 py-2 rounded-lg bg-[#0974BA]/100 text-white">Baixar arquivo</a></div>}
            </div>
          </div>
        </div>
      )}

      {/* OMR Card Preview Modal (with A4 and A5 2x1 options) */}
      <OMRCardPreview2x1
        isOpen={isPreviewOpen}
        onClose={closePreview}
        examId={examData.examId}
        examName={examData.examName}
        questions={examData.questions}
      />
    </>
  );
}

export default Provas;
